import { jsPDF } from "jspdf";
import { formatMoney } from "./formatMoney";

export interface ApprovalEventForPdf {
  who: string;
  when: string;
  action: "submit" | "approve" | "reject" | "send_back" | "sign";
  /** Nazwa etapu i jego formuła — dopasowane do zdarzenia (nie po indeksie). */
  stepName?: string;
  documentFormula?: string;
}

export interface MetricWorkflowStep {
  name: string;
  documentFormula?: string;
}

export interface EngagementMetricInput {
  title: string;
  signature?: string;
  engagementDate: string;
  executionDeadline: string;
  grantingProcedure: string;
  amount: number;
  divisionSymbol?: string;
  divisionName?: string;
  chapterSymbol?: string;
  chapterName?: string;
  timeline: ApprovalEventForPdf[];
  workflowSteps?: MetricWorkflowStep[];
}

const ACTION_LABELS: Record<string, string> = {
  submit: "Złożono wniosek",
  approve: "Zaakceptowano",
  reject: "Odrzucono",
  send_back: "Odesłano do poprawy",
  sign: "Podpisano elektronicznie",
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function generateEngagementMetricPdf(
  data: EngagementMetricInput,
): Promise<Blob> {
  if (!fontRegularB64)
    fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)
    fontBoldB64 = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const L = 14;
  const R = 196;
  const W = 182;
  let y = 16;

  const setF = (
    size: number,
    bold = false,
    r = 28,
    g = 27,
    b = 31,
  ) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(r, g, b);
  };

  const hline = () => {
    doc.setLineWidth(0.35);
    doc.setDrawColor(93, 73, 199);
    doc.line(L, y, R, y);
  };

  // ── Nagłówek dokumentu ─────────────────────────────────────────────
  setF(13, true, 93, 73, 199);
  doc.text("METRYKA ZAANGAŻOWANIA ŚRODKÓW BUDŻETOWYCH", L, y);
  y += 4;
  hline();
  y += 7;

  // ── Klasyfikacja budżetowa (pierwsza sekcja) ───────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("KLASYFIKACJA BUDŻETOWA", L, y);
  y += 6;

  const clsValX = L + 26;
  setF(8, true, 100, 90, 120);
  doc.text("DZIAŁ:", L, y);
  setF(9, false, 28, 27, 31);
  const divText = data.divisionSymbol
    ? `${data.divisionSymbol}${data.divisionName ? "  —  " + data.divisionName : ""}`
    : "—";
  const divLines = doc.splitTextToSize(divText, W - 26);
  doc.text(divLines, clsValX, y);
  y += divLines.length * 5 + 1.5;

  setF(8, true, 100, 90, 120);
  doc.text("ROZDZIAŁ:", L, y);
  setF(9, false, 28, 27, 31);
  const chapText = data.chapterSymbol
    ? `${data.chapterSymbol}${data.chapterName ? "  —  " + data.chapterName : ""}`
    : "—";
  const chapLines = doc.splitTextToSize(chapText, W - 26);
  doc.text(chapLines, clsValX, y);
  y += chapLines.length * 5 + 4;

  hline();
  y += 7;

  // ── Dane zaangażowania ─────────────────────────────────────────────
  setF(8, true, 100, 90, 120);
  doc.text("TYTUŁ:", L, y);
  setF(9.5, true, 28, 27, 31);
  const titleLines = doc.splitTextToSize(data.title, W - 22);
  doc.text(titleLines, L + 22, y);
  y += titleLines.length * 5 + 3;

  // Tabela danych - wiersz 1
  const col1x = L;
  const col2x = L + 90;
  const labelW = 38;

  const drawMetaRow = (
    k1: string, v1: string,
    k2: string, v2: string,
  ) => {
    setF(7.5, true, 120, 110, 140);
    doc.text(k1, col1x, y);
    setF(9, false, 28, 27, 31);
    doc.text(v1, col1x + labelW, y);
    setF(7.5, true, 120, 110, 140);
    doc.text(k2, col2x, y);
    setF(9, false, 28, 27, 31);
    doc.text(v2, col2x + labelW, y);
    y += 5.5;
  };

  drawMetaRow(
    "SYGNATURA:", data.signature ?? "—",
    "DATA:", fmtDate(data.engagementDate),
  );
  drawMetaRow(
    "KWOTA:", formatMoney(data.amount),
    "TERMIN:", fmtDate(data.executionDeadline),
  );

  setF(7.5, true, 120, 110, 140);
  doc.text("TRYB ZAMÓWIENIA:", col1x, y);
  setF(9, false, 28, 27, 31);
  const procLines = doc.splitTextToSize(data.grantingProcedure, W - labelW);
  doc.text(procLines, col1x + labelW, y);
  y += procLines.length * 5 + 5;

  hline();
  y += 7;

  // ── Tytuł sekcji procedury ─────────────────────────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("PRZEBIEG PROCEDURY AKCEPTACJI", L, y);
  y += 7;

  // ── Kroki timeline ─────────────────────────────────────────────────
  data.timeline.forEach((ev, i) => {
    // Zgodność wstecz: stare wywołania podają workflowSteps (po indeksie) — zdarzenie
    // niesie własny etap, gdy wołający je dopasował.
    const legacy = ev.stepName === undefined ? data.workflowSteps?.[i] : undefined;
    const stepName =
      ev.stepName ?? legacy?.name ?? ACTION_LABELS[ev.action] ?? ev.action;
    const formula = ev.documentFormula ?? legacy?.documentFormula;
    const actionLabel = ACTION_LABELS[ev.action] ?? ev.action;

    if (y > 260) {
      doc.addPage();
      y = 16;
    }

    // Numer etapu + nazwa
    setF(8, true, 120, 110, 140);
    doc.text(`Etap ${i + 1}`, L, y);
    y += 4.5;

    setF(10, true, 28, 27, 31);
    const nameLines = doc.splitTextToSize(stepName, W - 6);
    doc.text(nameLines, L + 3, y);
    y += nameLines.length * 5 + 1;

    // Formuła tekstowa (jeśli jest)
    if (formula?.trim()) {
      doc.setFillColor(248, 246, 255);
      const formulaLines = doc.splitTextToSize(formula, W - 12);
      const blockH = formulaLines.length * 4.5 + 4;
      doc.rect(L + 3, y - 1, W - 3, blockH, "F");
      doc.setLineWidth(0.5);
      doc.setDrawColor(93, 73, 199);
      doc.line(L + 3, y - 1, L + 3, y - 1 + blockH);
      setF(8.5, false, 60, 50, 80);
      doc.text(formulaLines, L + 7, y + 2);
      y += blockH + 3;
    }

    // Dane wykonawcy
    const dataRows: [string, string][] = [
      ["Osoba:", ev.who],
      ["Data:", fmtDateTime(ev.when)],
      ["Wynik:", actionLabel],
    ];
    for (const [label, value] of dataRows) {
      setF(7.5, true, 140, 130, 155);
      doc.text(label, L + 6, y);
      setF(8.5, false, 40, 40, 40);
      doc.text(value, L + 22, y);
      y += 4.5;
    }

    y += 4;

    // Separator między etapami (poza ostatnim)
    if (i < data.timeline.length - 1) {
      doc.setLineWidth(0.15);
      doc.setDrawColor(220, 215, 235);
      doc.line(L + 3, y - 2, R, y - 2);
      y += 2;
    }
  });

  y += 3;
  hline();
  y += 8;

  // ── Stopka ────────────────────────────────────────────────────────
  setF(7.5, false, 130, 120, 140);
  doc.text(`Data wydruku: ${new Date().toLocaleDateString("pl-PL")}`, L, y);
  doc.text(".......................................", R, y, { align: "right" });
  y += 4.5;
  setF(7, false, 170, 165, 180);
  doc.text("podpis i pieczęć", R, y, { align: "right" });

  return doc.output("blob");
}
